import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react'
import { ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ATTACHMENT_MAX_COUNT,
  ATTACHMENT_MAX_BYTES,
  extractEmailBodyFromDrag,
  extractEmlLink,
  extractUrlFromDrag,
  inferMime,
  isAttachableFile,
  isEmlFile,
  kindForMime,
  readFileAsBase64,
  snapshotDragTransfer,
  type PendingAttachment,
} from '../attachments'
import { ConciergeAttachmentChips } from './ConciergeAttachmentChips'

interface Props {
  onSubmit: (content: string, attachments: PendingAttachment[]) => void
  disabled?: boolean
  placeholder?: string
  /** Controlled composer value. Lifted to the parent so option/form cards
   *  can stage their answer text directly into the input. The component
   *  still owns local typing UX (paste, drop, auto-resize); the parent just
   *  owns the source of truth. */
  value: string
  onValueChange: (next: string) => void
  /** Monotonically-increasing counter. When it changes, the textarea is
   *  focused (and cursor moved to end). Used by cards to put the cursor in
   *  the composer after a click without forcing focus on every keystroke. */
  focusSignal?: number
}

/**
 * Spare composer matching the homepage's restraint — hairline top
 * border separator on the parent, no full border around the input
 * itself, no rounded corners. Enter submits, Shift+Enter newline.
 * iOS keyboard scroll-jump guard mirrors ZinsserChatInput.
 *
 * Attachments — JPG/PNG/GIF/WebP images and PDF/DOCX documents, 5 MB
 * each, 10 per turn — can be pasted (Cmd-V of a clipboard image) or
 * dropped onto the input. The handlers + accepted-types list mirror
 * the Obsidian plugin's `ConciergeView` exactly so the same drops work
 * on both surfaces.
 *
 * Drag also handles email links from Mail.app: when the drag has a
 * `message://` URL in `text/uri-list` and an HTML body in `text/html`,
 * we paste a markdown link AND the extracted body into the input.
 * Falls back to link-only when the body isn't included (some Mail.app
 * threaded-view drags omit it).
 *
 * Drag of a `.eml` *file* (from Finder / iOS Files / a download) is
 * handled separately — we parse its headers, build a `message://` link
 * from the Message-ID, and drop the link in the textarea. The file
 * itself is never uploaded.
 */

/** dropEffect MUST be set to a value compatible with effectAllowed
 *  or Safari silently cancels the drop. Mail.app desktop drags
 *  advertise effectAllowed = "link" (the drag is "make a reference
 *  to this message", not "copy bytes"); hardcoding 'copy' there
 *  makes Safari kill the drop with no event fired. We pick the
 *  right dropEffect off what the source offers. */
function matchDropEffect(dt: DataTransfer | null): void {
  if (!dt) return
  const allowed = dt.effectAllowed
  dt.dropEffect =
    allowed === 'link' || allowed === 'linkMove'
      ? 'link'
      : allowed === 'move'
        ? 'move'
        : 'copy'
}
export function ConciergeChatInput({
  onSubmit,
  disabled,
  placeholder,
  value,
  onValueChange,
  focusSignal,
}: Props) {
  const setValue = onValueChange
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollPositionRef = useRef<number>(0)
  const dragDepthRef = useRef(0)
  const [isDragHover, setDragHover] = useState(false)

  // Auto-grow up to 200px tall.
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
  }, [value])

  // Focus the textarea when the parent bumps `focusSignal` (cards stage
  // their answer into the composer and want the cursor placed in the
  // input so the user can press Enter to send or extend the staged text
  // before sending). Cursor goes to end-of-value so they're appending,
  // not overwriting.
  useEffect(() => {
    if (focusSignal === undefined) return
    const ta = textareaRef.current
    if (!ta) return
    ta.focus()
    const end = ta.value.length
    try {
      ta.setSelectionRange(end, end)
    } catch {
      /* setSelectionRange throws on some pre-mounted elements — ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSignal])

  // Errors fade after 4s so the surface stays calm.
  useEffect(() => {
    if (!error) return
    const t = window.setTimeout(() => setError(null), 4000)
    return () => window.clearTimeout(t)
  }, [error])

  const handleFocus = useCallback(() => {
    scrollPositionRef.current = window.scrollY
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollPositionRef.current) > 50) {
          window.scrollTo(0, scrollPositionRef.current)
        }
      })
    })
  }, [])

  // Single code path for "the user just dragged or pasted N files".
  // Filters rejected types, applies the count cap, base64-encodes
  // each accepted file, and surfaces a single human error line when
  // anything got dropped. `.eml` files are siphoned off first — they
  // become message:// links injected into the textarea, never
  // attachments.
  const addFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return
      const errors: string[] = []

      const eml = files.filter(isEmlFile)
      const nonEml = files.filter((f) => !isEmlFile(f))
      if (eml.length) {
        const pieces: string[] = []
        for (const f of eml) {
          try {
            const link = await extractEmlLink(f)
            if (link) {
              pieces.push(
                link.label && link.label !== link.url
                  ? `[${link.label}](${link.url})`
                  : link.url
              )
            } else {
              errors.push(`${f.name || 'eml'}: no Message-ID in headers`)
            }
          } catch {
            errors.push(`${f.name || 'eml'}: read failed`)
          }
        }
        if (pieces.length) {
          const insert = pieces.join('\n')
          const prev = value
          const sep = prev && !/[\s\n]$/.test(prev) ? ' ' : ''
          setValue(prev + sep + insert)
          requestAnimationFrame(() => textareaRef.current?.focus())
        }
      }

      const accepted: File[] = []
      for (const f of nonEml) {
        if (!isAttachableFile(f)) {
          if (f.size > ATTACHMENT_MAX_BYTES) {
            errors.push(`${f.name || 'file'} > 5MB`)
          } else {
            errors.push(`${f.name || 'file'}: unsupported type`)
          }
          continue
        }
        accepted.push(f)
      }
      // Apply count cap against the currently-pending list, not the
      // batch — dropping 8 + 5 should keep 8 + 2 (not 8 + 5 = 13).
      const remaining = ATTACHMENT_MAX_COUNT - attachments.length
      const toAdd = accepted.slice(0, Math.max(0, remaining))
      if (accepted.length > toAdd.length) {
        errors.push(`max ${ATTACHMENT_MAX_COUNT} per turn`)
      }

      const next: PendingAttachment[] = []
      for (const f of toAdd) {
        try {
          const mime = inferMime(f)
          const data_base64 = await readFileAsBase64(f)
          next.push({
            kind: kindForMime(mime),
            mime,
            filename: f.name || '',
            size: f.size,
            data_base64,
          })
        } catch (e) {
          errors.push(`${f.name || 'file'}: read failed`)
        }
      }

      if (next.length) {
        setAttachments((cur) => [...cur, ...next])
      }
      if (errors.length) {
        setError(errors.join(' · '))
      }
    },
    [attachments.length, value, setValue]
  )

  // Paste — covers Cmd-V of a clipboard image (Mac screenshots), Cmd-V
  // of a file copied from Finder, OR Cmd-V of plain text. Files take
  // precedence; if any are present we preventDefault so the textarea
  // doesn't also receive a `file://` paste. Plain-text paste falls
  // through to the textarea's default handler — same as today.
  const onPaste = useCallback(
    async (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const cd = e.clipboardData
      if (!cd || !cd.items) return
      const files: File[] = []
      for (const item of Array.from(cd.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile()
          if (f) files.push(f)
        }
      }
      if (files.length) {
        e.preventDefault()
        await addFiles(files)
      }
    },
    [addFiles]
  )

  const onDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    matchDropEffect(e.dataTransfer)
    dragDepthRef.current += 1
    if (dragDepthRef.current === 1) setDragHover(true)
  }, [])
  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setDragHover(false)
  }, [])
  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    matchDropEffect(e.dataTransfer)
  }, [])

  const handleDropData = useCallback(
    async (dt: DataTransfer | null) => {
      if (!dt) {
        // eslint-disable-next-line no-console
        console.warn('[concierge drop] no dataTransfer on event')
        return
      }

      // Unconditional snapshot to console — temporary, lets us see
      // exactly what each browser exposes for Mail.app drags without
      // requiring the user to set a localStorage flag first.
      // eslint-disable-next-line no-console
      console.log('[concierge drop] received', snapshotDragTransfer(dt))

      // 0) message:// URL takes precedence over any virtual .eml in
      //    dt.files. Mail.app desktop drags carry BOTH — a file-promise
      //    that often fails to read until macOS materializes the bytes
      //    AND a message:// in text/uri-list. The URL is always
      //    reliable and resolves to the exact same message either
      //    way. (iOS Mail/Files drag carries only a real .eml file —
      //    no URL — so it falls through to the file path below.)
      const link = extractUrlFromDrag(dt)
      if (link && link.url.startsWith('message://')) {
        const linkMd =
          link.label && link.label !== link.url
            ? `[${link.label}](${link.url})`
            : link.url
        let insert = linkMd
        const body = extractEmailBodyFromDrag(dt)
        if (body) {
          insert = `${linkMd}\n\n--- Email body ---\n${body}\n--- End email body ---`
        }
        const prev = value
        const sep = prev && !/[\s\n]$/.test(prev) ? ' ' : ''
        setValue(prev + sep + insert)
        requestAnimationFrame(() => textareaRef.current?.focus())
        return
      }

      // 1) Files — same precedence as the Obsidian plugin. Real .eml
      //    drops (Finder, iOS Files) land here and `addFiles` parses
      //    the headers into a message:// link.
      const files = dt.files ? Array.from(dt.files) : []
      if (files.length) {
        await addFiles(files)
        return
      }

      // 2) Non-message URLs (Safari https://, etc.) → markdown link.
      //    message:// URLs are handled in block 0 above; any URL that
      //    reaches here is by definition some other protocol, so no
      //    body-extraction branch is needed.
      if (link) {
        const linkMd =
          link.label && link.label !== link.url
            ? `[${link.label}](${link.url})`
            : link.url
        const prev = value
        const sep = prev && !/[\s\n]$/.test(prev) ? ' ' : ''
        setValue(prev + sep + linkMd)
        requestAnimationFrame(() => textareaRef.current?.focus())
        return
      }

      // 3) Fallback — Mail.app sometimes provides an HTML body but
      //    no surviving message:// URL when dropped into browsers
      //    (especially Safari). Pull the body alone so the agent
      //    still has the email content to work with.
      const body = extractEmailBodyFromDrag(dt)
      if (body) {
        const insert = `--- Email body ---\n${body}\n--- End email body ---`
        {
          const prev = value
          const sep = prev && !/[\s\n]$/.test(prev) ? ' ' : ''
          setValue(prev + sep + insert)
        }
        requestAnimationFrame(() => textareaRef.current?.focus())
        return
      }

      // 4) Last resort — drop plain text as-is. Better to land
      //    SOMETHING in the textarea than silently swallow the
      //    drop and leave the user wondering. (Mail.app at a
      //    minimum provides the subject + "From:" line via
      //    text/plain even when the URL is stripped.)
      let plain = ''
      try {
        plain = dt.getData('text/plain') || ''
      } catch {
        /* Safari can throw on getData for non-protected types */
      }
      if (plain.trim()) {
        {
          const prev = value
          const sep = prev && !/[\s\n]$/.test(prev) ? ' ' : ''
          setValue(prev + sep + plain.trim())
        }
        requestAnimationFrame(() => textareaRef.current?.focus())
        return
      }

      // Nothing usable. Surface a hint rather than silently doing
      // nothing — the previous behavior of failed Mail.app drops.
      // eslint-disable-next-line no-console
      console.warn('[concierge drop] nothing extractable from drop')
      setError(
        "Couldn't read the drop. Open devtools console — the drop's available data is logged there. For Mail.app try right-click → Share → Copy Link, then Cmd-V in the textbox."
      )
    },
    // `value` and `setValue` are read directly when appending dropped text
    // — must stay in deps so the closure sees the latest composer state.
    [addFiles, value, setValue]
  )

  // De-dupe flag: set true the moment the element-level handler fires,
  // checked by the window-level handler so we don't double-process a
  // drop that already reached the React tree. Reset on a microtask
  // boundary so the window handler (which runs after React synthetic
  // events on the same frame) reads the latest value.
  const elementHandledRef = useRef(false)

  // Element-level drop (when the user lands ON the input container)
  // — visual state (dragHover) is also managed here.
  const onDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      elementHandledRef.current = true
      dragDepthRef.current = 0
      setDragHover(false)
      try {
        await handleDropData(e.dataTransfer)
      } finally {
        // Tiny delay so the window-level handler (which runs in the
        // same tick on most browsers) sees the flag, then clear it.
        window.setTimeout(() => {
          elementHandledRef.current = false
        }, 100)
      }
    },
    [handleDropData]
  )

  // Window-level drop catcher — for cases where the browser doesn't
  // dispatch the drop event to our React tree (some Mail.app drags
  // into Safari/Chrome behave this way because the surrounding page
  // never preventDefault'd dragover, so the drop becomes "external"
  // navigation). Window listeners run AFTER React's synthetic events.
  // We skip when elementHandledRef is true to avoid double-processing.
  useEffect(() => {
    const readDt = (e: Event) =>
      (e as unknown as { dataTransfer: DataTransfer | null }).dataTransfer
    const onWindowDragOver = (e: Event) => {
      // Required for the drop event to fire on the window. Without
      // preventDefault on dragover, the browser rejects the drop and
      // does its own URL navigation / file open. dropEffect must also
      // match effectAllowed or Safari kills Mail.app drops silently.
      e.preventDefault()
      matchDropEffect(readDt(e))
    }
    const onWindowDrop = async (e: Event) => {
      e.preventDefault()
      if (elementHandledRef.current) return
      const dt = readDt(e)
      // eslint-disable-next-line no-console
      console.log('[concierge drop] caught at window level (element handler missed)')
      await handleDropData(dt ?? null)
    }
    window.addEventListener('dragover', onWindowDragOver)
    window.addEventListener('drop', onWindowDrop)
    return () => {
      window.removeEventListener('dragover', onWindowDragOver)
      window.removeEventListener('drop', onWindowDrop)
    }
  }, [handleDropData])

  const removeAttachment = useCallback((index: number) => {
    setAttachments((cur) => cur.filter((_, i) => i !== index))
  }, [])

  const submit = () => {
    const v = value.trim()
    if (!v && !attachments.length) return
    if (disabled) return
    onSubmit(v, attachments)
    setValue('')
    setAttachments([])
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    })
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter always inserts a newline regardless of device (hardware
    // keyboards on any platform), so we only need to decide what plain
    // Enter does. On touch-primary devices (phones/tablets) iOS/Android
    // keyboards don't surface Shift+Enter, so plain Enter has to mean
    // "newline" or the user can never multi-line; submit via the ↑ button
    // instead. On fine-pointer devices (desktop) keep the classic chat
    // behavior: Enter submits, Shift+Enter newlines.
    if (e.key === 'Enter' && !e.shiftKey) {
      if (window.matchMedia('(pointer: coarse)').matches) return
      e.preventDefault()
      submit()
    }
  }

  return (
    <div
      className={cn(
        'concierge-input-container',
        isDragHover && 'concierge-input-drag-hover'
      )}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <ConciergeAttachmentChips
        items={attachments}
        onRemove={removeAttachment}
      />
      {error && <div className="concierge-input-error">{error}</div>}
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          onFocus={handleFocus}
          onPaste={onPaste}
          placeholder={placeholder || 'Talk to the Concierge…'}
          disabled={disabled}
          rows={1}
          className={cn(
            'flex-1 bg-transparent border-0 outline-none resize-none leading-relaxed',
            'text-foreground placeholder:text-muted-foreground/60 disabled:opacity-50',
            'max-h-[200px] min-h-[24px] py-1'
          )}
          // 16px font on iOS to prevent auto-zoom on focus
          style={{ fontSize: '16px' }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={(!value.trim() && !attachments.length) || disabled}
          className={cn(
            'flex-shrink-0 p-1 transition-colors',
            (value.trim() || attachments.length) && !disabled
              ? 'text-foreground hover:opacity-70'
              : 'text-muted-foreground/30 cursor-not-allowed'
          )}
          aria-label="Send"
        >
          <ArrowUp size={18} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  )
}
